import { api } from './api';
import type { PaginatedResponse } from './secretary';

// ─── Primitive union types ─────────────────────────────────────────────────────
// Values match exactly the Django TextChoices in apps/attendances/models/.

export type AttendanceChannel = 'whatsapp' | 'chat' | 'phone' | 'in_person' | 'email' | 'classapp';

export type AttendanceStatus =
  | 'waiting'
  | 'active'
  | 'paused'
  | 'escalated'
  | 'awaiting_nps'
  | 'completed'
  | 'closed_inactivity'
  | 'closed_contact'
  | 'cancelled';

export type AgentType = 'ai' | 'human';

export type MessageRole = 'contact' | 'agent' | 'system' | 'operator';

export type MessageType =
  | 'text'
  | 'image'
  | 'document'
  | 'audio'
  | 'video'
  | 'location'
  | 'system'
  | 'satisfaction'
  /** Nota da equipe — nunca vai ao contato nem ao agente (decisão 0012). */
  | 'internal_note';

export type CloseReason =
  | 'resolved'
  | 'inactivity'
  | 'contact_request'
  | 'escalated'
  | 'system'
  | 'operator';

export type ActionType =
  | 'request_created'
  | 'request_updated'
  | 'request_status_changed'
  | 'card_created'
  | 'card_updated'
  | 'card_moved'
  | 'info_provided'
  | 'faq_answered'
  | 'document_shared'
  | 'escalated'
  | 'satisfaction_sent'
  | 'satisfaction_received'
  | 'contact_identified'
  | 'intent_detected';

// ─── ApiSessionListItem ────────────────────────────────────────────────────────
// Matches SessionListSerializer exactly.

export type ApiWaitingState = '' | 'sector' | 'family' | 'ready_to_close';

export interface ApiSessionListItem {
  id: number;
  protocol_number: string;
  school: string;                   // UUID
  school_name: string;
  channel: AttendanceChannel;
  channel_display: string;
  status: AttendanceStatus;
  status_display: string;
  agent_type: AgentType;
  agent_type_display: string;
  agent_user: number | null;        // User PK
  agent_username: string | null;
  /** '' = ainda na triagem central (visível a qualquer operador); preenchido
   *  = setor específico (Board.sector_key), visível só a operadores desse
   *  setor + manager/coordinator/superuser. Ver forward-to-sector. */
  sector_key: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  siga_user_id: string | null;
  message_count: number;
  action_count: number;
  request_count: number;
  has_evaluation: boolean;
  /** Inbox compartilhada — não é por usuário: qualquer operador que já abriu
   *  o detalhe (GET /sessions/{id}/) limpa pra todo mundo. Mesma regra de
   *  SessionListSerializer.get_has_unread()/SessionViewSet.unread_count(). */
  has_unread: boolean;
  /** Janela de 24h do WhatsApp (Meta) — null para canais que não são
   *  whatsapp (telefone/presencial/e-mail não têm essa noção). Hoje
   *  (UazAPI) não bloqueia nada — é só aviso; passa a valer de verdade
   *  quando a escola migrar pra cloud_api. */
  contact_window_open: boolean | null;
  /** Setores com pedido de ajuda ABERTO nesta sessão (decisão 0012, regra 25). */
  open_help_sectors?: string[];
  /** Algum pedido aberto é para um setor de quem está vendo — visão "Ajuda". */
  help_for_me?: boolean;
  /** Fila "sem dono" (decisão 0012, regra 28): desde quando, minutos úteis
   *  de espera e se já passou de 15 (atrasada). null quando tem dono/IA. */
  awaiting_owner_since?: string | null;
  waiting_minutes?: number | null;
  overdue?: boolean;
  /** Sem dono e perto de estourar a meta (ou já estourada) — a coordenação já foi avisada. */
  critical?: boolean;
  /** Etiquetas de assunto da conversa (ConversationTag), em ordem alfabética. */
  tags?: ApiConversationTag[];
  /** Adiada (snooze) até este horário — null quando não está adiada (ou o
   *  prazo já venceu). Some das visões da fila e fica em "Adiadas". */
  snoozed_until?: string | null;
  snoozed_by?: number | null;
  /** Mensagens agendadas pendentes pro contato (selo na fila). */
  scheduled_count?: number;
  /** Espera fora da fila (ADR 0022), calculada pela API a partir dos pedidos:
   *  'sector'/'family' = em espera (sai da fila ativa e do contador);
   *  'ready_to_close' = todos os pedidos terminaram, de volta na fila. */
  waiting_state?: ApiWaitingState;
  waiting_sector_key?: string;
  waiting_sector_name?: string;
  waiting_since?: string | null;
  started_at: string;               // ISO 8601
  first_response_at: string | null;
  ended_at: string | null;
  duration_seconds: number | null;
  // ── Histórico: do que se tratava, quem atendeu e como terminou ──
  /** Assunto do registro → resumo do encerramento → 1ª mensagem da família.
   *  Vazio quando não há nada (a tela usa o canal). */
  title?: string;
  /** Assunto digitado no registro manual (ligação/visita/e-mail/ClassApp). */
  subject?: string;
  outcome?: SessionOutcome | '';
  outcome_display?: string;
  initiated_by?: SessionInitiatedBy | '';
  close_reason?: CloseReason | '';
  close_reason_display?: string;
  agent_name?: string | null;
  closed_by_name?: string | null;
  ai_involved?: boolean;
  rating?: number | null;
}

/** Como terminou um registro manual — "resolved" e "no_answer" já encerram. */
export type SessionOutcome = 'resolved' | 'follow_up' | 'no_answer';
/** Quem procurou quem no registro manual. */
export type SessionInitiatedBy = 'contact' | 'school';

// ─── ApiSessionMessage ────────────────────────────────────────────────────────
// Matches SessionMessageSerializer exactly.

export interface ApiReplySuggestion {
  id: number;
  text: string;
  /** Fontes que a IA usou — vazio quando não achou nada na FAQ/documentos. */
  sources: { kind: 'faq' | 'documento'; title: string }[];
  grounded: boolean;
}

export interface ApiForwardTarget {
  id: string;
  name: string;
  sector_key: string;
}

export interface ApiSessionMessage {
  id: number;
  role: MessageRole;
  role_display: string;
  message_type: MessageType;
  message_type_display: string;
  content: string;
  external_message_id: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

// ─── ApiSessionAction ─────────────────────────────────────────────────────────
// Matches SessionActionSerializer exactly.

/** Referência a um BoardCard real por trás de uma ação/solicitação — alimenta
 *  o peek do card (CardPeekDialog) em vez de abrir direto o histórico da
 *  conversa. Mesmo shape resolvido pelo backend em SessionActionSerializer.
 *  get_card_info e no endpoint /attendances/sessions/open-items/. */
export interface CardInfoRef {
  id: number;
  board_id: string;
  is_triage_board: boolean;
}

export interface ApiSessionAction {
  id: number;
  action_type: ActionType;
  action_type_display: string;
  description: string;
  target_type: string | null;       // 'app_label.model', e.g. 'requests.request'
  target_object_id: number | null;
  confidence: number | null;
  metadata: Record<string, unknown>;
  is_reversed: boolean;
  reversed_by: number | null;       // User PK
  reversed_by_username: string | null;
  reversed_at: string | null;
  /** Usuário humano que executou a ação — null quando foi o agente/IA. */
  actor: number | null;
  actor_username: string | null;
  created_at: string;
  /** Só preenchido para action_type='request_created' com card ainda não
   *  arquivado — resolvido via metadata.request_id no backend. Alimenta o
   *  botão "Ver card" no chip da timeline (ver TimelineEntryRow, shared.tsx). */
  card_info: CardInfoRef | null;
}

// ─── ApiSessionRequest ────────────────────────────────────────────────────────
// Matches SessionRequestSummarySerializer exactly.
// Used for the inline `requests[]` field in ApiSessionDetail.

export interface ApiSessionRequest {
  id: number;
  request_type: string;
  request_type_display: string;
  status: string;
  status_display: string;
  siga_user_name: string;
  requester_name: string;
  guardian_name: string | null;
  student_name: string | null;
  created_at: string;
}

// ─── ApiSessionDocument ───────────────────────────────────────────────────────
// Matches SessionDocumentSummarySerializer exactly.
// Used for the inline `documents[]` field in ApiSessionDetail — SchoolDocument
// (declaração/histórico/transferência) não é um Request, tem sua própria FK
// para session (SchoolDocument.session) e seu próprio resumo no backend.

export interface ApiSessionDocument {
  id: string;               // UUID
  protocol: string;
  document_type: string;
  document_type_display: string;
  status: string;
  status_display: string;
  title: string;
  due_date: string | null;
  guardian_name: string | null;
  student_name: string | null;
  created_at: string;
}

// ─── ApiSessionDetail ─────────────────────────────────────────────────────────
// Matches SessionDetailSerializer (extends SessionListSerializer).
// Returned by GET /attendances/sessions/{id}/ e by close/escalate actions.

// Matches SessionEvaluationSummarySerializer (eleveia-api) — nota/comentário
// reais da Evaluation vinculada (nested, não confundir com o binário
// ApiSessionListItem.has_evaluation).
export interface ApiSessionEvaluation {
  rating: number;
  comment: string;
  nps_category: 'promoter' | 'neutral' | 'detractor';
  created_at: string;
}

/** Solicitação unificada (Request + SchoolDocument) — decisão 0012, fase 4a. */
export interface ApiSolicitation {
  key: string;              // "req-<id>" | "doc-<id>"
  kind: 'request' | 'document';
  id: string;
  type_label: string;
  student_name: string;
  status: 'nova' | 'aberta' | 'aguardando_familia' | 'aguardando_setor' | 'resolvida' | 'fechada';
  is_open: boolean;
  sector_key: string;
  sector_name: string;
  owner: { id: number; username: string; full_name: string } | null;
  waiting_on: '' | 'family' | 'sector';
  session_id: number | null;
  created_at: string | null;
  updated_at: string | null;
  /** Lista do contato (topo + lateral do Atendimento) — ver listSolicitations. */
  protocol?: string;
  parts?: ApiSolicitationPart[];
  /** Card para o detalhe na lateral: o da Triagem (mesmo arquivado), senão o do setor. */
  card_id?: string | null;
  board_id?: string | null;
  completed_at?: string | null;
}

/** Uma parte = o trabalho de UM setor numa solicitação (o card no board dele). */
export interface ApiSolicitationPart {
  sector_key: string;
  sector_name: string;
  board_id: string;
  card_id: string;
  list_title: string;
  state: 'pendente' | 'andamento' | 'concluido';
  assignee_name: string;
  due_date: string | null;
  overdue: boolean;
}

export type SchoolSolicitationView = 'sem_setor' | 'confirmar' | 'andamento' | 'atrasadas' | 'familia' | 'concluidas';

/** Linha da tela "Solicitações" (gestão) — GET /attendances/solicitations/?scope=school. */
export interface ApiSchoolSolicitation extends ApiSolicitation {
  protocol: string;
  contact_name: string;
  contact_phone: string;
  parts: ApiSolicitationPart[];
  parts_total: number;
  parts_done: number;
  overdue: boolean;
  /** Prioridade alta/urgente (conforme a escola configurou) e em aberto — a coordenação já foi avisada. */
  critical?: boolean;
  priority?: string;
  /** Minutos úteis sem setor nem dono (só para pedidos sem setor). */
  waiting_minutes: number;
  triage_card_id: string | null;
  /** Board e coluna do card da Triagem — o drawer abre direto, sem carregar a Triagem. */
  triage_board_id: string | null;
  triage_list_status: string;
  /** O card da Triagem foi arquivado (limpeza diária) — o drawer abre mesmo assim. */
  triage_card_archived?: boolean;
  /** O setor devolveu e a gestão precisa confirmar (validação ligada pro tipo). */
  awaiting_confirmation: boolean;
  return_reason: '' | 'completed' | 'wrong_sector' | 'missing_info' | 'partial_step';
  view: Exclude<SchoolSolicitationView, 'atrasadas'>;
}

export interface ApiSchoolSolicitationList {
  counts: Record<SchoolSolicitationView, number>;
  count: number;
  results: ApiSchoolSolicitation[];
}

// ─── Etiquetas e relatório de tempos ──────────────────────────────────────────
// ConversationTagViewSet / SessionViewSet.set_tags / SessionViewSet.report.

/** Paleta fixa — mesma ordem de TAG_COLORS no backend. */
export const TAG_COLORS = ['slate', 'red', 'orange', 'amber', 'green', 'teal', 'blue', 'indigo', 'purple', 'pink'] as const;
export type TagColor = (typeof TAG_COLORS)[number];

export interface ApiConversationTag {
  id: number;
  name: string;
  color: TagColor;
  is_archived: boolean;
  /** Só na listagem de /tags/: quantas conversas usam. */
  usage_count?: number;
  /** Regras (só gestor muda) — rodam quando a conversa recebe a etiqueta.
   *  Encaminhar: só se a conversa espera humano na recepção (ou quando a IA escalar). */
  auto_forward_sector_key?: string;
  notify?: TagNotify;
  /** Meta própria para assumir (min) — vale se for menor que a da escola. */
  target_minutes?: number | null;
}

export type TagNotify = '' | 'managers' | 'sector';

/** Mediana/p90 em minutos de horário escolar; null sem dados no período. */
export interface ApiTimeStat {
  n: number;
  mediana_min: number | null;
  p90_min: number | null;
  dentro_da_meta_pct: number | null;
}

export interface ApiReportRow {
  conversas: number;
  encerradas: number;
  primeira_resposta: ApiTimeStat;
  resolucao: ApiTimeStat;
}

export interface ApiAttendanceReport {
  periodo: { date_from: string; date_to: string };
  meta_minutos: number;
  resumo: {
    conversas: number;
    por_canal: Record<string, number>;
    encerradas: number;
    com_humano: number;
    resolvidas_pela_ia_pct: number | null;
    ate_assumir: ApiTimeStat;
    primeira_resposta: ApiTimeStat;
    resolucao: ApiTimeStat;
  };
  por_atendente: (ApiReportRow & { user_id: number; name: string })[];
  por_setor: (ApiReportRow & { sector_key: string; name: string })[];
  por_etiqueta: (ApiReportRow & { id: number; name: string; color: TagColor })[];
  sem_etiqueta: number;
  por_dia: { dia: string; recebidas: number; com_humano: number; resolvidas_pela_ia: number }[];
}

/** GET /sessions/queue-summary/ — resumo da fila no escopo de quem pede. */
export interface ApiQueueSummary {
  overdue_threshold_minutes: number;
  in_business_hours: boolean;
  sem_dono: number;
  atrasadas: number;
  com_ia: number;
  em_atendimento: number;
  minhas: number;
  /** Adiadas — não contam em sem dono/atrasadas/minhas. */
  adiadas?: number;
  /** Em espera (aguardando setor/família) — fora da fila ativa. */
  em_espera?: number;
  /** Mediana, em minutos, do tempo sem dono até alguém assumir — hoje. */
  tempo_ate_assumir_mediana_min: number | null;
  assumidas_hoje: number;
  encerradas_hoje: number;
  /** % das encerradas hoje que nunca passaram por humano. */
  resolvidas_pela_ia_pct: number | null;
  por_setor: { sector_key: string; name: string; abertas: number; sem_dono: number; atrasadas: number }[];
  esperando_ha_mais_tempo: {
    id: number; contact_name: string; contact_phone: string; sector_key: string;
    waiting_minutes: number; overdue: boolean;
  }[];
}

export interface ApiSnoozeOption {
  key: 'in_1h' | 'tomorrow' | 'next_school_day' | 'next_week';
  label: string;
  until: string;
}

export interface ApiHelpRequest {
  id: number;
  sector_key: string;
  question: string;
  status: 'open' | 'answered';
  requested_by: { id: number; username: string; full_name: string } | null;
  answered_by: { id: number; username: string; full_name: string } | null;
  answer: string;
  answered_at: string | null;
  created_at: string;
}

export interface ApiSessionDetail extends ApiSessionListItem {
  help_requests?: ApiHelpRequest[];
  close_reason: CloseReason | '';
  close_reason_display: string;
  escalation_reason: string;
  escalated_at: string | null;
  escalated_to: number | null;      // User PK
  escalated_to_username: string | null;
  /** Nome de quem assumiu (o username sozinho não é o que a equipe lê). */
  escalated_to_name?: string | null;
  /** Motivo que a IA deu na última vez que passou o atendimento para um
   *  humano — não é sobrescrito quando alguém clica "Assumir". */
  ai_escalation_reason?: string;
  ai_escalated_at?: string | null;
  summary: string;
  metadata: Record<string, unknown>;
  messages: ApiSessionMessage[];
  actions: ApiSessionAction[];
  requests: ApiSessionRequest[];
  documents: ApiSessionDocument[];
  evaluation: ApiSessionEvaluation | null;
}

// ─── ApiRequestListItem ───────────────────────────────────────────────────────
// Matches RequestListSerializer (apps/requests/serializers.py).
// Returned by GET /attendances/sessions/{id}/requests/.

export interface ApiRequestListItem {
  id: number;
  protocol: string;
  school_name: string;
  session_id: number | null;
  siga_user_id: string | null;
  siga_user_name: string;
  request_type: string;
  request_type_display: string;
  status: string;
  status_display: string;
  requires_approval: boolean;
  created_by_username: string;
  created_at: string;
  completed_at: string | null;
  has_card: boolean;
  // Triagem "Por Atendimento" (TriagemAtendimentosView) — setor de destino +
  // estágio do card ligado a esta solicitação. Sempre null quando
  // has_card=false (nunca gerou card ainda, ou foi arquivado/removido).
  sector_key: string | null;
  triage_status: 'none' | 'pending_triage' | 'routed' | 'returned' | 'completed' | null;
  card_id: number | null;
  board_id: string | null;
  // True quando o card em si vive no board de Triagem Central — mesmo campo
  // já usado em CardInfoRef/ApiCard (boards.ts).
  is_triage_board: boolean;
  // Id do card de Triagem a abrir para "Ver na Triagem" — o próprio card
  // (quando is_triage_board) OU o card de origem, se este for um card de
  // setor despachado/escalado pela Triagem (mesma precedência do botão "Ver
  // na Triagem" do CardPeekDialog.tsx). null se nunca teve relação com a
  // Triagem — nesse caso não há drawer de Triagem pra abrir, só o peek.
  triage_open_card_id: number | null;
}

// ─── ApiContactOpenItem ───────────────────────────────────────────────────────
// Matches GET /attendances/sessions/open-items/?phone=... — agrega Request e
// SchoolDocument não finais de um contato através de TODAS as suas sessões
// (não só a mais recente). Cada item carrega `session_id`/`protocol_number`
// pra permitir abrir o atendimento de origem (ver ContactProfile.tsx).

export interface ApiContactOpenItem {
  key: string;
  kind: 'request' | 'document';
  label: string;
  status_display: string;
  created_at: string;
  session_id: number | null;
  protocol_number: string;
  /** Protocolo da própria solicitação (ex.: "SEC-2026-0012"); `protocol_number` é o da conversa. */
  protocol?: string;
  /** Card real por trás do item (quando já existe e não está arquivado) —
   *  clicar no item abre o peek desse card em vez do histórico da conversa. */
  card_info: CardInfoRef | null;
}

export interface ApiContactOpenItemsResponse {
  count: number;
  items: ApiContactOpenItem[];
}

/** GET /attendances/sessions/family-summary/ — "Resumo da família" da lateral. */
export interface ApiFamilySummary {
  found: boolean;
  financial?: { overdue_count: number; overdue_total: string; oldest_due_date: string | null } | null;
  open_requests?: { count: number; oldest_at: string | null };
  captacao?: {
    lead_id: number;
    protocol: string | null;
    status: string;
    status_display: string;
    visit_scheduled_at: string | null;
    candidates: { name: string; grade: string }[];
  } | null;
  recent_sessions?: {
    id: number;
    started_at: string | null;
    channel: string;
    channel_display: string;
    subject: string;
    protocol: string;
  }[];
  last_evaluation?: { rating: number; created_at: string | null } | null;
  memory?: string[];
}

// ─── SessionStats ─────────────────────────────────────────────────────────────
// Matches the response of GET /attendances/sessions/stats/.

// ─── Unread count ───────────────────────────────────────────────────────────
// Matches GET /attendances/sessions/unread-count/ — inbox compartilhada entre
// operadores (não é por usuário): conta sessões com mensagem do contato mais
// recente que a última visualização do detalhe por qualquer operador.

export interface SessionUnreadCount {
  total: number;
  by_channel: Record<string, number>;
}

export interface SessionStats {
  total_sessions: number;
  by_status: Record<string, number>;
  by_channel: Record<string, number>;
  by_agent_type: Record<string, number>;
  performance: {
    avg_duration_seconds: number;
    avg_messages_per_session: number;
    avg_actions_per_session: number;
  };
  total_requests_generated: number;
}

// ─── Timeline types ───────────────────────────────────────────────────────────
// Matches the response of GET /attendances/sessions/{id}/timeline/.

export interface ApiTimelineEvent {
  type: 'message' | 'action' | 'request_created';
  timestamp: string;
  data: ApiSessionMessage | ApiSessionAction | ApiSessionRequest;
}

export interface ApiTimelineResponse {
  session_id: number;
  protocol_number: string;
  total_events: number;
  timeline: ApiTimelineEvent[];
}

// ─── Session requests response ────────────────────────────────────────────────
// Matches the response of GET /attendances/sessions/{id}/requests/.

export interface ApiSessionRequestsResponse {
  session_id: number;
  protocol_number: string;
  request_count: number;
  requests: ApiRequestListItem[];
}

// ─── Request bodies ───────────────────────────────────────────────────────────

/** Decisão da janela de encerramento para um pedido (ADR 0022, etapa c):
 *  marcado = completed; desmarcado = not_completed + motivo; em outro setor
 *  e "Aguardar o setor" = wait. */
export type CloseDecision =
  | { key: string; outcome: 'completed' }
  | { key: string; outcome: 'not_completed'; reason: string; detail?: string }
  | { key: string; outcome: 'wait' };

export interface CloseSessionBody {
  reason: CloseReason;
  summary?: string;
  /** Com pedido aberto: uma decisão por pedido. Algum "wait" → a conversa
   *  fica "Aguardando setor" em vez de encerrar. */
  decisions?: CloseDecision[];
}

/** Mesmo limite do backend (BulkSessionActionSerializer.MAX_BULK_SESSIONS). */
export const BULK_LIMIT = 50;

export type BulkActionBody = { session_ids: number[] } & (
  | { action: 'close'; reason: CloseReason; summary?: string }
  /** operator_id null = devolver à fila. */
  | { action: 'assign'; operator_id: number | null }
  | { action: 'add_tags' | 'remove_tags'; tag_ids: number[] }
  /** until null = desfazer o adiamento. */
  | { action: 'snooze'; until: string | null }
  /** sector_key '' = devolver à recepção. */
  | { action: 'forward'; sector_key: string; note: string }
);

export interface BulkActionResult {
  action: BulkActionBody['action'];
  succeeded: number[];
  failed: { id: number; detail: string }[];
}

export interface EscalateSessionBody {
  operator_id?: number | null;
  reason?: string;
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const attendancesService = {
  listSessions: (params?: Record<string, string>) =>
    api.get<PaginatedResponse<ApiSessionListItem>>('/attendances/sessions/', { params }),

  getSession: (id: number) =>
    api.get<ApiSessionDetail>(`/attendances/sessions/${id}/`),

  getTimeline: (id: number) =>
    api.get<ApiTimelineResponse>(`/attendances/sessions/${id}/timeline/`),

  getSessionRequests: (id: number) =>
    api.get<ApiSessionRequestsResponse>(`/attendances/sessions/${id}/requests/`),

  /** Registra uma SessionAction genérica na timeline do atendimento — usado
   *  hoje pela resolução manual de identidade (action_type='contact_identified',
   *  ver AtendimentoForm.tsx). actor é sempre resolvido pelo backend a partir
   *  de request.user, nunca aceito neste payload. */
  registerAction: (
    sessionId: number,
    payload: { action_type: string; description: string; metadata?: Record<string, unknown> },
  ) => api.post<ApiSessionAction>(`/attendances/sessions/${sessionId}/action/`, payload),

  /** Solicitações/documentos abertos de um contato através de todo o histórico
   *  (não só a sessão mais recente) — alimenta "Solicitações abertas" no
   *  painel de perfil (ContactProfile.tsx). */
  getOpenItemsByContact: (phone: string) =>
    api.get<ApiContactOpenItemsResponse>('/attendances/sessions/open-items/', { params: { phone } }),

  getFamilySummary: (phone: string, excludeSessionId?: number) =>
    api.get<ApiFamilySummary>('/attendances/sessions/family-summary/', {
      params: excludeSessionId ? { phone, exclude_session: String(excludeSessionId) } : { phone },
    }),

  getStats: (params?: Record<string, string>) =>
    api.get<SessionStats>('/attendances/sessions/stats/', { params }),

  getUnreadCount: () =>
    api.get<SessionUnreadCount>('/attendances/sessions/unread-count/'),

  closeSession: (id: number, body: CloseSessionBody = { reason: 'operator' }) =>
    api.post<ApiSessionDetail & { close_result?: 'closed' | 'waiting' }>(`/attendances/sessions/${id}/close/`, body),

  /** `ai_pause_status`: 'requested' = pausa da IA pedida; 'failed' = não
   *  saiu (a IA pode responder junto); null = não houve troca IA→humano. */
  escalateSession: (id: number, body?: EscalateSessionBody) =>
    api.post<ApiSessionDetail & { ai_pause_status?: 'requested' | 'failed' | null }>(
      `/attendances/sessions/${id}/escalate/`, body ?? {},
    ),

  /** "Pegar próximo": assume o atendimento sem dono que espera há mais
   *  tempo. 404 = fila vazia. */
  /** Rascunho de resposta pela IA (FAQ + documentos + conversa). Nada é
   *  enviado à família. `recent` = o que o painel mostra da conversa. */
  suggestReply: (sessionId: number, recent: { from: 'contact' | 'school'; text: string }[]) =>
    api.post<ApiReplySuggestion>(`/attendances/sessions/${sessionId}/suggest-reply/`, { recent_messages: recent }),

  /** Pergunta livre da equipe ao copiloto (FAQ + documentos + conversa). Responde a quem atende, nunca à família. */
  askCopilot: (sessionId: number, question: string) =>
    api.post<{ answer: string; sources: ApiReplySuggestion['sources']; grounded: boolean }>(
      `/attendances/sessions/${sessionId}/ask-copilot/`, { question },
    ),

  markSuggestionUsed: (sessionId: number, suggestionId: number) =>
    api.post<{ used: boolean }>(`/attendances/sessions/${sessionId}/suggest-reply/${suggestionId}/used/`, {}),

  /** Adia a conversa até `until` (ISO) — some da fila e volta sozinha no
   *  horário, ou antes se a família escrever. null desfaz. */
  snoozeSession: (id: number, until: string | null) =>
    api.post<ApiSessionDetail>(`/attendances/sessions/${id}/snooze/`, { until }),

  /** "Tirar da espera" (ADR 0022): volta para a fila até um pedido mudar. */
  releaseWaiting: (id: number) =>
    api.post<ApiSessionDetail>(`/attendances/sessions/${id}/release-waiting/`, {}),

  /** Atalhos do "Adiar" no horário escolar da escola. */
  getSnoozeOptions: () =>
    api.get<ApiSnoozeOption[]>('/attendances/sessions/snooze-options/'),

  takeNext: () =>
    api.post<ApiSessionDetail & { ai_pause_status?: 'requested' | 'failed' | null }>(
      '/attendances/sessions/take-next/', {},
    ),

  /** Disponível/Ausente — ausente não recebe o aviso de atendimento que
   *  precisa de alguém (a não ser que ninguém esteja disponível). */
  getMyStatus: () => api.get<{ available: boolean }>('/attendances/sessions/my-status/'),
  setMyStatus: (available: boolean) =>
    api.patch<{ available: boolean }>('/attendances/sessions/my-status/', { available }),

  /** Encaminha a sessão (da triagem central ou de outro setor) para um setor
   *  específico — o backend zera agent_user (fica sem dono na fila do setor
   *  de destino até alguém assumir via escalate/assignToMe, sem mudança lá)
   *  e já registra a mensagem de sistema na timeline. */
  /** Setores para onde qualquer atendente pode encaminhar (boards sector_main
   *  da escola) — não depende de ser membro do board, ao contrário de GET /boards/. */
  getForwardTargets: () =>
    api.get<ApiForwardTarget[]>('/attendances/sessions/forward-targets/'),

  /** Quem pode ficar com um atendimento (manager/operator ativos da escola) —
   *  lista do "Atribuir". Ao contrário de /auth/profiles/school_users/ (só
   *  gestor), o operador também consegue ler. */
  getAssignableUsers: () =>
    api.get<{ results: { id: number; user: number; full_name: string }[] }>('/attendances/sessions/assignable-users/'),

  /** `note` = motivo do encaminhamento, obrigatório para pessoas (decisão 0012). */
  forwardToSector: (id: number, sectorKey: string, note: string) =>
    api.post<ApiSessionDetail>(`/attendances/sessions/${id}/forward-to-sector/`, { sector_key: sectorKey, note }),

  /** Devolve o atendimento ao agente IA (caminho simétrico ao escalate/take-over). */
  resumeAi: (id: number) =>
    api.post<ApiSessionDetail>(`/attendances/sessions/${id}/resume-ai/`, {}),

  updateSession: (id: number, data: Record<string, unknown>) =>
    api.patch<ApiSessionDetail>(`/attendances/sessions/${id}/`, data),

  /**
   * Corrige o nome do contato exibido na Central de Atendimento. Endpoint
   * dedicado, não PATCH genérico — SessionListSerializer/SessionDetailSerializer
   * têm todos os campos read-only, então updateSession() acima não teria efeito
   * nenhum em contact_name (mesmo bug já visto em agent_user, ver CLAUDE.md).
   */
  updateContactName: (id: number, contactName: string) =>
    api.post<ApiSessionDetail>(`/attendances/sessions/${id}/update-contact-name/`, { contact_name: contactName }),

  /**
   * Adiciona uma mensagem ao histórico da sessão — mensagem de sistema (ex.:
   * nota de encaminhamento) ou nota de operador (canal manual: ligação/
   * presencial/portal). `metadata.sent_by` é quem dá a atribuição visível na
   * timeline (ver MessageBubble/parseSentBy) — o backend não estampa isso
   * sozinho, então quem chama com role='operator' precisa passar o usuário.
   */
  addMessage: (id: number, body: { role: MessageRole; content: string; message_type?: MessageType; metadata?: Record<string, unknown> }) =>
    api.post<ApiSessionMessage>(`/attendances/sessions/${id}/message/`, body),

  /** Solicitações do contato: abertas + encerradas nos últimos 3 dias; com
   *  `history`, todas. Lê o status do pedido, nunca o arquivamento do card. */
  listSolicitations: (contactPhone: string, opts: { history?: boolean } = {}) =>
    api.get<{ count: number; results: ApiSolicitation[] }>('/attendances/solicitations/', {
      params: { contact_phone: contactPhone, ...(opts.history ? { history: '1' } : {}) },
    }),
  listSchoolSolicitations: (view: SchoolSolicitationView, q = '') =>
    api.get<ApiSchoolSolicitationList>('/attendances/solicitations/', {
      params: { scope: 'school', view, ...(q ? { q } : {}) },
    }),
  confirmSolicitation: (key: string) =>
    api.post<ApiSolicitation>(`/attendances/solicitations/${key}/confirm/`, {}),
  addSolicitationSector: (key: string, sectorKey: string, note = '') =>
    api.post<ApiSolicitation>(`/attendances/solicitations/${key}/add-sector/`, { sector_key: sectorKey, note }),
  assumeSolicitation: (key: string) =>
    api.post<ApiSolicitation>(`/attendances/solicitations/${key}/assume/`, {}),
  /** sectorKey '' = devolver à recepção. Nota obrigatória (regra 22), exceto
   *  no 1º encaminhamento (sem setor e sem dono). */
  transferSolicitation: (key: string, sectorKey: string, note: string) =>
    api.post<ApiSolicitation>(`/attendances/solicitations/${key}/transfer/`, { sector_key: sectorKey, note }),
  setSolicitationWaiting: (key: string, on: '' | 'family' | 'sector') =>
    api.post<ApiSolicitation>(`/attendances/solicitations/${key}/wait/`, { on }),
  resolveSolicitation: (key: string, note: string) =>
    api.post<ApiSolicitation>(`/attendances/solicitations/${key}/resolve/`, { note }),

  /** Resumo da fila (fase 3): sem dono, atrasadas, por setor, tempo até assumir. */
  getQueueSummary: () => api.get<ApiQueueSummary>('/attendances/sessions/queue-summary/'),

  /** Etiquetas da escola; includeArchived só na tela de gestão. */
  listTags: (includeArchived = false) =>
    api.get<ApiConversationTag[]>('/attendances/tags/', {
      params: includeArchived ? { include_archived: '1' } : undefined,
    }),
  createTag: (name: string, color: TagColor) =>
    api.post<ApiConversationTag>('/attendances/tags/', { name, color }),
  /** Só gestor (renomear, cor, arquivar, regras). */
  updateTag: (id: number, data: Partial<Pick<ApiConversationTag,
    'name' | 'color' | 'is_archived' | 'auto_forward_sector_key' | 'notify' | 'target_minutes'>>) =>
    api.patch<ApiConversationTag>(`/attendances/tags/${id}/`, data),
  /** Só gestor, e só etiqueta nunca usada (usada → arquivar). */
  deleteTag: (id: number) => api.delete(`/attendances/tags/${id}/`),
  /** Ação em massa (até BULK_LIMIT conversas): cada uma passa pelas mesmas
   *  regras da ação individual — o resultado diz quais deram certo e por que
   *  as outras não. */
  bulkAction: (body: BulkActionBody) =>
    api.post<BulkActionResult>('/attendances/sessions/bulk/', body),

  /** Substitui as etiquetas da conversa. */
  setSessionTags: (sessionId: number, tagIds: number[]) =>
    api.post<{ tags: ApiConversationTag[] }>(`/attendances/sessions/${sessionId}/tags/`, { tag_ids: tagIds }),

  /** Relatório de tempos (gestor/coordenação). Datas AAAA-MM-DD locais. */
  getReport: (dateFrom: string, dateTo: string) =>
    api.get<ApiAttendanceReport>('/attendances/sessions/report/', {
      params: { date_from: dateFrom, date_to: dateTo },
    }),

  /** Pede ajuda a outro setor sem transferir (decisão 0012, regra 25). */
  requestHelp: (id: number, sectorKey: string, question: string) =>
    api.post<ApiHelpRequest>(`/attendances/sessions/${id}/help-request/`, { sector_key: sectorKey, question }),

  /** Resposta do setor consultado — fecha o pedido. */
  answerHelp: (id: number, helpId: number, content: string) =>
    api.post<ApiHelpRequest>(`/attendances/sessions/${id}/help-requests/${helpId}/answer/`, { content }),

  /** Nota interna da equipe (decisão 0012): o contato não recebe e a IA não
   *  lê. O backend grava o autor a partir do usuário logado. */
  /** `solicitationKey` ("req-<id>" | "doc-<id>"): também comenta no card
   *  dessa solicitação, marcado "Nota da conversa". */
  addInternalNote: (id: number, content: string, solicitationKey?: string | null) =>
    api.post<ApiSessionMessage>(
      `/attendances/sessions/${id}/internal-note/`,
      solicitationKey ? { content, solicitation_key: solicitationKey } : { content },
    ),

  /** ELE-147 — Envia mensagem de resposta do operador via WhatsApp. */
  sendReply: (id: number, content: string) =>
    api.post<{ message: ApiSessionMessage; sent_to_whatsapp: boolean }>(
      `/attendances/sessions/${id}/reply/`,
      { content, message_type: 'text' },
    ),
};

// ─── Backward-compat aliases ──────────────────────────────────────────────────
// The Atendimentos.tsx written in the previous session uses these names.
// They will be replaced with canonical types in the S2 UI refactor session.

/** @deprecated Use AttendanceChannel */
export type SessionChannel = AttendanceChannel;

/** @deprecated Use AttendanceStatus */
export type SessionStatus = AttendanceStatus;

/**
 * @deprecated Use ApiSessionMessage.
 * Extends with legacy role literals ('user' | 'assistant') that the current
 * Atendimentos.tsx compares against. S2 will migrate to MessageRole.
 */
export type SessionMessage = Omit<ApiSessionMessage, 'role'> & {
  role: MessageRole | 'user' | 'assistant';
};

/**
 * @deprecated Use ApiSessionRequest.
 * Adds optional `protocol` field accessed by the current Atendimentos.tsx.
 * S2 will migrate to ApiSessionRequest (no protocol in SessionRequestSummarySerializer).
 */
export type SessionRequest = ApiSessionRequest & { protocol?: string };

/**
 * @deprecated Use ApiSessionListItem | ApiSessionDetail.
 * Compatibility shim so the existing Atendimentos.tsx compiles while S2 is pending.
 * Extra fields (protocol, created_at, closed_at, rating, rating_comment) are absent
 * at runtime — they are aliases for protocol_number, started_at, ended_at and fields
 * not yet exposed by the backend. S2 will remove this type.
 */
export type ServiceSession = ApiSessionListItem & {
  protocol?: string;
  created_at?: string;
  closed_at?: string | null;
  rating?: number | null;
  rating_comment?: string | null;
  messages?: SessionMessage[];
  actions?: ApiSessionAction[];
  requests?: SessionRequest[];
};
